import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";

import {
	Modal,
	Button,
	ProgressIndicator
} from "rhombus-react";

import { loadCreatives, fetchCreativesById } from "reducers/Creatives";
import { confirmImport, confirmImportWithPreview, reset } from "reducers/ImportVersions";
import { clearActiveVariable, clearCropMetadata, clearRefinements } from "reducers/Refinements";

import VersionImportTable from "./VersionImportTable";

import "./ImportConfirmationModal.css";

export default function ImportConfirmationModal(props){
	const dispatch = useDispatch();
	const {
		jobId,
		libraryId,
		creatives,
		onBack,
		onClose,
	} = props;

	const { importVersionsRefinements } = useSelector(state => state.refinements);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [areCreativesLoaded, setAreCreativesLoaded] = useState(false);
	const [fullCreativesById, setFullCreativesById] = useState({});

	useEffect(() => {
		const creativeIds = creatives.map(creative => creative.id);
		dispatch(fetchCreativesById(libraryId, creativeIds)).then((response) => {
			if(response?.success){
				const fullCreatives = response?.payload?.data?.items || [];
				const mappedById = fullCreatives.reduce((prev, creative) => {
					return {
						...prev,
						[creative.id]: creative,
					};
				}, {});
				setFullCreativesById(mappedById);
			}

			/*
				This is set after setFullCreativesById so that we don't get a render of the table
				before the mapped data is ready, so we don't get Sentry errors about the approval status
				being unknown.
			*/
			setAreCreativesLoaded(true);
		});
	}, [creatives]);

	const mappedCreatives = useMemo(() => {
		return creatives.map((creative) => {
			return {
				...creative,
				approval: fullCreativesById[creative.id]?.approval,
			};
		});
	}, [creatives, fullCreativesById]);

	function onConfirm(){
		setIsSubmitting(true);
		/*
			Confirm import without preview if there are no refinements.
			Otherwise, confirm import with preview.
		*/
		if(isEmpty(importVersionsRefinements)){
			dispatch(confirmImport(jobId)).then((response) => {
				setIsSubmitting(false);
				if(response?.success){
					dispatch(loadCreatives(libraryId));
					onCloseModal();
				}
			});
		} else {
			dispatch(confirmImportWithPreview(jobId)).then((response) => {
				setIsSubmitting(false);
				if(response?.success){
					dispatch(loadCreatives(libraryId));
					onCloseModal();
				}
			});
		}
	}

	function Content(){
		return (
			<div className="progress-container">
				<ProgressIndicator isLoading={!areCreativesLoaded || isSubmitting} />
				<p>
					Confirm version import for the creative listed below
				</p>
				{areCreativesLoaded && <VersionImportTable creatives={mappedCreatives} />}
			</div>
		);
	}
	function Footer(){
		return (
			<>
				<div className="left-buttons">
					<Button theme="default" onClick={onCloseModal}>Cancel Import</Button>
					<Button theme="default" onClick={onBack}>Back to Preview</Button>
				</div>
				<Button theme="green" onClick={onConfirm} disabled={isSubmitting} >Confirm Import</Button>
			</>
		);
	}

	function onCloseModal() {
		// Clear all Crop and refinement related metadata if any
		dispatch(clearActiveVariable());
		dispatch(clearCropMetadata());
		dispatch(clearRefinements());
		dispatch(reset());
		onClose();
	}

	let modalProps = {
		title: "Import Confirmation",
		type: "modal",
		className: "version-import-confirmation-modal",
		setAppTitle: false,
		xClose: true,
		allowFullScreen: false,
		bladeProps: {
			verticallyCenter: true,
		},
		renderFooter: Footer,
		renderContent: Content,
		onClose: onCloseModal,
	};

	return <Modal {...modalProps} />;
}

ImportConfirmationModal.displayName = "ImportConfirmationModal";
ImportConfirmationModal.propTypes = {
	jobId: PropTypes.string,
	libraryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	creatives: PropTypes.arrayOf(PropTypes.shape({
		name: PropTypes.string.isRequired,
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		new: PropTypes.number,
		updated: PropTypes.number,
	})),
	onBack: PropTypes.func,
	onClose: PropTypes.func,
};
